import { AmountUsd } from '@ui/components/Amount/AmountUsd';
import { Pnl } from '@ui/components/Amount/Pnl';
import { usePayTokenPriceLatest } from '@ui/features/Trade/hooks/usePayTokenPriceLatest';
import { usePriceLive } from '@ui/hooks/price/usePriceLive';
import { useTradePairInfosByPairBase } from '@ui/hooks/trade/useTradePairInfos';
import { Text } from '@ui/lib/park/ui/text';
import { Position } from '@ui/services/contract/hooks/TradingReaderFacet/useGetPositionV2/types';
import { omit } from 'lodash-es';
import { useTranslation } from 'react-i18next';
import { Box, HStack, Stack } from 'styled-system/jsx';

import { CloseFee } from './CloseFee';
import { usePositionListContext } from './context';
import { FeeLearnMoreLink } from './FeeLearnMoreLink';
import { FundingFeeDisplay } from './FundingFeeDisplay';
import { InitialCollateral } from './InitialCollateral';
import { valueStyle } from './styles';
import { PositionMarketData } from './types';

export const NetValueTooltipContent = ({
  position,
  marketData,
}: {
  position: Position;
  marketData?: PositionMarketData;
}) => {
  if (marketData) {
    return (
      <NetValueTooltipContentValue
        position={position}
        marketData={marketData}
      />
    );
  }

  return <NetValueTooltipContentWithHooks position={position} />;
};

const NetValueTooltipContentWithHooks = ({
  position,
}: {
  position: Position;
}) => {
  const collateralPrice = usePayTokenPriceLatest(position.collateralToken);
  const pairMarketPrice = usePriceLive(position.pair.baseSymbol);
  const tradePair = useTradePairInfosByPairBase(position.pair.address);

  return (
    <NetValueTooltipContentValue
      position={position}
      marketData={{
        collateralMarketPrice: collateralPrice,
        pairMarketPrice,
        tradePair,
      }}
    />
  );
};

const NetValueTooltipContentValue = ({
  position,
  marketData,
}: {
  position: Position;
  marketData: PositionMarketData;
}) => {
  const { t } = useTranslation();
  const { feeConfig } = usePositionListContext();
  const collateralPrice = marketData.collateralMarketPrice;
  const pairMarketPrice = marketData.pairMarketPrice;
  const tradePair = marketData.tradePair;

  if (!tradePair) return null;

  const netValue =
    feeConfig &&
    pairMarketPrice &&
    collateralPrice &&
    position.getNetValue({
      collateralMarketPrice: collateralPrice,
      pairMarketPrice: pairMarketPrice,
      tradePair,
      ...feeConfig,
    });

  return (
    <Stack minW="60">
      <Box>
        <HStack
          justify="space-between"
          borderBottom="1px solid"
          borderBottomColor="greyWhite.outline"
          pb={2}
          textStyle="headings.small"
        >
          <Text color="greyWhite.white">{t('common.Net Value')}</Text>

          <AmountUsd
            value={netValue}
            data-cy-value={netValue}
            data-cy="NetValueTooltip-NetValue"
          />
        </HStack>
        {/* <FeeLearnMoreLink mt={5} /> */}
      </Box>

      <Stack gap={4}>
        <Stack gap={1}>
          <HStack justify="space-between">
            <Text css={valueStyle} maxW="52" textAlign="left">
              {t('common.Initial Collateral (After open fee)')}
            </Text>
            <InitialCollateral
              position={position}
              marketPrice={collateralPrice}
              data-cy="NetValueTooltip-InitialCollateral"
            />
          </HStack>

          <HStack justify="space-between">
            <Text css={valueStyle}>{t('common.PnL')}</Text>
            <Pnl
              value={
                pairMarketPrice &&
                position.getPnl({
                  pairMarketPrice,
                  tradePair,
                })
              }
              css={omit(valueStyle, 'color')}
              opts={{ isShowSign: true }}
              data-cy="NetValueTooltip-PnL"
            />
          </HStack>

          <HStack justify="space-between">
            <Text css={valueStyle}>{t('common.Funding Fee')}</Text>
            <FundingFeeDisplay
              position={position}
              collateralMarketPrice={collateralPrice}
              data-cy="NetValueTooltip-FundingFee"
            />
          </HStack>
          <HStack justify="space-between">
            <Text css={valueStyle}>{t('common.Close Fee')}</Text>
            <CloseFee
              position={position}
              marketData={marketData}
              data-cy="NetValueTooltip-CloseFee"
            />
          </HStack>
        </Stack>
      </Stack>
    </Stack>
  );
};
